import { useEffect, useRef, useState } from 'react';
import { bookmarkletCode, shortcutCode } from '../lib/bookmarklet';
import { Sheet } from './Sheet';

const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="btn ink"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
        } catch {
          setCopied(false);
        }
      }}
    >
      {copied ? 'Kopiert' : label}
    </button>
  );
}

/** Explains and hands out the browser button: an iOS shortcut or a bookmark. */
export function BrowserButtonSheet({ onClose }: { onClose: () => void }) {
  const bookmark = bookmarkletCode(location.origin);
  const shortcut = shortcutCode(location.origin);
  const linkRef = useRef<HTMLAnchorElement>(null);

  // React refuses javascript: URLs in href, so it is set on the element directly
  useEffect(() => {
    linkRef.current?.setAttribute('href', bookmark);
  }, [bookmark]);

  return (
    <Sheet open onClose={onClose} onSubmit={onClose} labelledBy="bbTitle">
      <h2 id="bbTitle">Browser-Knopf</h2>
      <p className="hint">
        Manche Shops (z. B. Bershka, Pull&amp;Bear, Adidas, ASOS) lassen Closetly nicht auf ihre Seiten. Der Knopf
        holt die Daten aus der Produktseite, die du gerade im Browser offen hast.
      </p>

      <details className="bb-steps" open={isIOS}>
        <summary>iPhone: Kurzbefehl (empfohlen)</summary>
        <p className="bb-lead">Danach reicht in Safari: Teilen, „Zu Closetly“.</p>
        <div className="bb-actions">
          <CopyButton text={shortcut} label="Code für Kurzbefehl kopieren" />
        </div>
        <ol>
          <li>
            Einstellungen, Apps, Kurzbefehle, Erweitert: das Ausführen von Skripten erlauben (nur einmal nötig).
          </li>
          <li>In der Kurzbefehle-App mit „+“ einen neuen Kurzbefehl anlegen und „Zu Closetly“ nennen.</li>
          <li>
            Über das Info-Symbol „Im Share-Sheet anzeigen“ einschalten und als Eingabe nur „Safari-Webseiten“ zulassen.
          </li>
          <li>
            Aktion „JavaScript auf Webseite ausführen“ hinzufügen, den Beispielcode löschen und den kopierten Code
            einfügen.
          </li>
          <li>Aktion „URLs öffnen“ dahinter setzen; als URL das „JavaScript-Ergebnis“ wählen.</li>
          <li>
            In Safari auf einer Produktseite: Teilen, „Zu Closetly“. Beim ersten Mal pro Shop fragt iOS, ob der
            Kurzbefehl die Seite lesen darf: erlauben.
          </li>
        </ol>
      </details>

      <details className="bb-steps">
        <summary>iPhone: Lesezeichen</summary>
        <div className="bb-actions">
          <CopyButton text={bookmark} label="Lesezeichen-Code kopieren" />
        </div>
        <ol>
          <li>Diese Seite als Lesezeichen sichern: Teilen-Symbol, „Lesezeichen hinzufügen“, Name „Zu Closetly“.</li>
          <li>Lesezeichen öffnen, „Bearbeiten“, „Zu Closetly“ wählen und die Adresse durch den kopierten Code ersetzen.</li>
          <li>Im Shop auf der Produktseite: Lesezeichen öffnen und „Zu Closetly“ tippen.</li>
        </ol>
      </details>

      <details className="bb-steps" open={!isIOS && matchMedia('(hover: none)').matches}>
        <summary>Android (Chrome)</summary>
        <div className="bb-actions">
          <CopyButton text={bookmark} label="Lesezeichen-Code kopieren" />
        </div>
        <ol>
          <li>Diese Seite als Lesezeichen sichern (Menü ⋮, Stern), dann das Lesezeichen bearbeiten.</li>
          <li>Name „Zu Closetly“, als URL den kopierten Code einfügen.</li>
          <li>
            Im Shop auf der Produktseite in die Adressleiste „Zu Closetly“ tippen und das Lesezeichen aus den Vorschlägen
            wählen.
          </li>
        </ol>
      </details>

      <details className="bb-steps" open={!matchMedia('(hover: none)').matches}>
        <summary>Computer</summary>
        <div className="bb-actions">
          <a
            ref={linkRef}
            className="btn"
            title="In die Lesezeichenleiste ziehen"
            onClick={(e) => e.preventDefault()}
            draggable
          >
            Zu Closetly
          </a>
        </div>
        <ol>
          <li>„Zu Closetly“ mit der Maus in die Lesezeichenleiste ziehen.</li>
          <li>Im Shop auf der Produktseite das Lesezeichen anklicken.</li>
        </ol>
      </details>

      <p className="hint">
        Shop-Apps öffnen Links in der App. Dann „Link kopieren“ und in Safari öffnen, oder den Link direkt in Closetly
        einfügen (dann nur Name und Shop). Der Knopf öffnet Closetly in Safari: dort einmal anmelden, danach erscheint
        alles auch in der App auf dem Home-Bildschirm.
      </p>

      <div className="sheet-actions">
        <div className="right">
          <button type="submit" className="btn ink">
            Fertig
          </button>
        </div>
      </div>
    </Sheet>
  );
}
